import Navbar from 'components/navbar';
import {
  generate15MonthPeriods,
  mockHistoricalSubmissions,
  mockNDCSearchResults,
  mockOOCHistory,
  mockOOCSubmissions,
} from 'data/fdp/oocMockData';
import { useStore } from 'hooks/useStore';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { toast } from 'react-toastify';
import { OOCNDCSearchResult, OOCTabType } from 'Interfaces/FDPInterfaces/oocInterfaces';

const OOCUpdatedHoc = () => {
  const { OOCStoreUpdated: OOCStore } = useStore();

  useEffect(() => {
    const store = OOCStore;

    const mockCounts = {
      all: mockOOCSubmissions.length,
      draft: mockOOCSubmissions.filter((s) => s.status === 'draft').length,
      communicated: mockOOCSubmissions.filter((s) => s.status === 'communicated').length,
      scReviewing: 0,
      history: mockOOCHistory.length,
    };

    // ── Seed initial mock state ───────────────────────────────
    runInAction(() => {
      store.submissions = [...mockOOCSubmissions];
      store.dashboardCounts = mockCounts;
      store.historyData = [...mockOOCHistory];
    });

    // ── Save originals for teardown ───────────────────────────
    const orig = {
      fetchSubmissions: store.fetchSubmissions,
      openSubmissionForEdit: store.openSubmissionForEdit,
      createNewSubmission: store.createNewSubmission,
      searchNDCs: store.searchNDCs,
      addNDCToCurrentSubmission: store.addNDCToCurrentSubmission,
      removeAdjustment: store.removeAdjustment,
      saveDraft: store.saveDraft,
      cancelAndSave: store.cancelAndSave,
      communicateToSC: store.communicateToSC,
      sendEmail: store.sendEmail,
      fetchHistory: store.fetchHistory,
      fetchHistoryDetail: store.fetchHistoryDetail,
      fetchAndApplyBaseline: store.fetchAndApplyBaseline,
      fetchProvForecastParams: store.fetchProvForecastParams,
    };

    // ── Override with mock implementations ────────────────────

    store.fetchSubmissions = async () => {
      runInAction(() => {
        const subs = store.submissions;
        store.dashboardCounts = {
          all: subs.length,
          draft: subs.filter((s) => s.status === 'draft').length,
          communicated: subs.filter((s) => s.status === 'communicated').length,
          scReviewing: 0,
          history: mockOOCHistory.length,
        };
        store.isLoading = false;
      });
    };

    store.openSubmissionForEdit = async (id: string) => {
      const submission = mockOOCSubmissions.find((s) => s.id === id) ?? null;
      runInAction(() => {
        store.currentSubmission = submission ? { ...submission, adjustments: [...submission.adjustments] } : null;
        store.activeTab = 'MAS';
        store.isLoading = false;
        store.isSubmissionDirty = false;
        if (submission?.status === 'draft') store.currentScreen = 'create';
        else if (submission?.status === 'communicated') store.currentScreen = 'communicate';
      });
    };

    store.createNewSubmission = async (submittedBy: string, planningPeriod: string) => {
      const newId = `OOC-MOCK-${Date.now()}`;
      const submission = {
        id: newId,
        reason: undefined,
        submissionDate: new Date().toLocaleDateString('en-US'),
        submittedBy,
        status: 'draft' as const,
        planningPeriod,
        contractTypes: [] as OOCTabType[],
        ndcCount: 0,
        adjustments: [],
      };
      runInAction(() => {
        store.submissions = [submission, ...store.submissions];
        store.currentSubmission = submission;
        store.currentScreen = 'create';
        store.activeTab = 'MAS';
        store.isLoading = false;
      });
      toast.success(`${newId} created as Draft`);
    };

    store.searchNDCs = async (signal?: AbortSignal) => {
      if (!store.ndcSearchQuery.trim()) {
        runInAction(() => { store.ndcSearchResults = []; });
        return;
      }
      runInAction(() => { store.isNDCSearching = true; });
      await new Promise((r) => setTimeout(r, 200));
      if (signal?.aborted) return;
      const q = store.ndcSearchQuery.toLowerCase();
      runInAction(() => {
        store.ndcSearchResults = mockNDCSearchResults.filter(
          (n) =>
            n.ndc11.toLowerCase().includes(q) ||
            n.molecule.toLowerCase().includes(q) ||
            n.concept.toLowerCase().includes(q),
        );
        store.isNDCSearching = false;
      });
    };

    store.addNDCToCurrentSubmission = async (ndc: OOCNDCSearchResult, selectedTab?: OOCTabType) => {
      if (!store.currentSubmission) return;
      const exists = store.currentSubmission.adjustments.some((a) => a.ndc11 === ndc.ndc11);
      if (exists) { toast.warn(`${ndc.ndc11} is already in this OOC submission`); return; }
      const targetTab: OOCTabType = selectedTab ?? 'MAS';
      const tabLabel =
        targetTab === 'MAS' ? 'MAS DP'
        : targetTab === 'NC' ? 'NC DP'
        : targetTab === 'ProvMAS' ? 'Prov. MAS'
        : 'Prov. NC';
      const newAdj = {
        id: `adj-mock-${Date.now()}`,
        oocId: store.currentSubmission.id,
        ndc10: ndc.ndc10,
        ndc11: ndc.ndc11,
        supplier: ndc.supplier,
        molecule: ndc.molecule,
        concept: ndc.concept,
        packOut: ndc.packOut,
        tab: targetTab,
        forecasts: generate15MonthPeriods().map((period) => ({
          period,
          value: 0,
          previousValue: null,
          colorType: 'new' as const,
        })),
        notes: '',
        reason: undefined,
        currentMonthValue: null,
      };
      runInAction(() => {
        if (!store.currentSubmission) return;
        store.currentSubmission = {
          ...store.currentSubmission,
          adjustments: [...store.currentSubmission.adjustments, newAdj],
          ndcCount: store.currentSubmission.adjustments.length + 1,
          contractTypes: Array.from(new Set([...store.currentSubmission.contractTypes, targetTab])),
        };
        store.activeTab = targetTab;
        store.isSubmissionDirty = true;
      });
      toast.success(`${ndc.ndc11} added to ${tabLabel} tab`);
    };

    store.removeAdjustment = async (adjustmentId: string) => {
      runInAction(() => {
        if (!store.currentSubmission) return;
        const updated = store.currentSubmission.adjustments.filter((a) => a.id !== adjustmentId);
        store.currentSubmission = { ...store.currentSubmission, adjustments: updated, ndcCount: updated.length };
      });
      toast.success('NDC removed from submission');
    };

    store.saveDraft = async () => {
      runInAction(() => { store.isSaving = true; });
      await new Promise((r) => setTimeout(r, 300));
      runInAction(() => { store.isSaving = false; store.isSubmissionDirty = false; });
      toast.success('Draft saved successfully');
    };

    store.cancelAndSave = async () => {
      if (store.currentSubmission) {
        const updated = store.currentSubmission;
        runInAction(() => {
          store.submissions = store.submissions.map((s) =>
            s.id === updated.id ? { ...updated } : s
          );
        });
      }
      store.navigateToDashboard();
    };

    store.communicateToSC = async () => {
      if (!store.currentSubmission) return;
      if (store.currentSubmission.adjustments.length === 0) {
        toast.warn('At least one NDC must be added before communicating to SC.');
        return;
      }
      const oocId = store.currentSubmission.id;
      runInAction(() => { store.isSaving = true; });
      await new Promise((r) => setTimeout(r, 400));
      runInAction(() => {
        if (!store.currentSubmission) return;
        const communicated = {
          ...store.currentSubmission,
          status: 'communicated' as const,
          communicatedDate: new Date().toLocaleDateString('en-US'),
        };
        store.currentSubmission = communicated;
        store.submissions = store.submissions.map((s) =>
          s.id === communicated.id ? { ...communicated } : s
        );
        store.currentScreen = 'communicate';
        store.isSaving = false;
      });
      toast.success(`${oocId} communicated to SC.`);
    };

    store.sendEmail = async () => {
      runInAction(() => { store.isSendingEmail = true; });
      await new Promise((r) => setTimeout(r, 300));
      runInAction(() => { store.isSendingEmail = false; });
      toast.success('Email sent successfully to SC, Purchasing, PPS, BD, and CSE.');
    };

    store.fetchHistory = async () => {
      runInAction(() => {
        store.historyData = [...mockOOCHistory];
        store.isHistoryLoading = false;
      });
    };

    store.fetchHistoryDetail = async (oocId: string) => {
      runInAction(() => { store.isHistoryDetailLoading = true; store.historyDetailSubmission = null; });
      await new Promise((r) => setTimeout(r, 200));
      const detail = mockHistoricalSubmissions.find((s) => s.id === oocId) ?? null;
      runInAction(() => {
        store.historyDetailSubmission = detail;
        store.isHistoryDetailLoading = false;
      });
    };

    store.fetchAndApplyBaseline = async (ndc11s: string[]) => {
      runInAction(() => {
        store.loadingBaselineNDCs = store.loadingBaselineNDCs.filter((n) => !ndc11s.includes(n));
      });
    };

    store.fetchProvForecastParams = async (ndc11: string) => {
      runInAction(() => {
        store.loadingProvForecastNDCs = store.loadingProvForecastNDCs.filter((n) => n !== ndc11);
      });
    };

    return () => {
      Object.assign(store, orig);
      store.resetStore();
    };
  }, [OOCStore]);

  return (
    <div>
      <Navbar heading="FDP - Out of Cycle" />
      <Outlet />
    </div>
  );
};

export default observer(OOCUpdatedHoc);
